import React, { FC, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { resetClassification, setClassificationSegment } from "../../../ducks/classification/classification";
import { getClassificationClassesCount } from "../../../ducks/classification/selectors";
import { goToClassification } from "../../../ducks/location/location";
import { useCurrentLayerTableName } from "../../../hooks/layer/useCurrentLayer";
import { useClassificationQueryParams } from "../../../hooks/location/useClassificationQueryParams";
import { useTable } from "../../../hooks/table/useTable";
import { useDialogReset } from "../../../hooks/useDialogs";
import { ClassesRangeCalculationBase } from "./components/ClassesRangeCalculationBase";
import { getSelectedSegment, getValueIndex } from "../../../ducks/style/selectors";
import { useClassificationSegment } from "./hooks/useClassificationSegment";
import { CalculateClassesPayload } from "./types";

export const ClassesRangeCalculation: FC = () => {
  const dispatch = useDispatch();
  const resetDialog = useDialogReset();
  const selectedSegment = useSelector(getSelectedSegment);
  const valueIndex = useSelector(getValueIndex);
  const classificationSegment = useClassificationSegment();
  const classesCountCurrent = useSelector(getClassificationClassesCount);
  const { type, parameter, attributeName, attributeType, valueIndex: queryValueIndex } = useClassificationQueryParams();
  const { table: currentTable } = useTable(useCurrentLayerTableName(), true);

  const getCount = useMemo(
    () => (selectedSegment === undefined ? classesCountCurrent : classificationSegment?.values?.length || 0),
    [classesCountCurrent, classificationSegment?.values?.length, selectedSegment],
  );

  const calculateClassesCount = useCallback(
    ({ classCount, algorithm }: CalculateClassesPayload) => {
      if (selectedSegment === undefined) {
        dispatch(resetClassification());
      } else {
        dispatch(setClassificationSegment({ [selectedSegment]: null }));
      }

      dispatch(
        goToClassification({
          parameter,
          field: attributeName,
          valueIndex: queryValueIndex || valueIndex,
          count: classCount,
          type: algorithm,
        }),
      );
    },
    [attributeName, dispatch, parameter, queryValueIndex, selectedSegment, valueIndex],
  );

  return (
    <ClassesRangeCalculationBase
      toggle={resetDialog}
      algorithm={type}
      attributeType={attributeType}
      count={getCount}
      calculateClassesCount={calculateClassesCount}
      tableRowCount={(currentTable && currentTable.rowCount) || 0}
    />
  );
};
